package vegasful.admin.components

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import vegasful.admin.Application
import vegasful.admin.Theme

object ButtonStyles : StyleSheet() {
    val button by style {
        position(Position.Relative)
        display(DisplayStyle.LegacyInlineFlex)
        backgroundColor(Theme.buttonBackgroundNormal.value())
        color(Theme.buttonForegroundNormal.value())
        border(1.px, LineStyle.Solid, Theme.buttonBorderNormal.value(Color.black))
        fontWeight("bold")
        padding((0.4).cssRem, (1.5).cssRem, (0.2).cssRem)
        cursor("pointer")
        fontSize((0.9).cssRem)

        self + hover style {
            backgroundColor(Theme.buttonBackgroundNormalHover.value())
            color(Theme.buttonForegroundNormalHover.value())
        }

        adjacent(self, self).style {
            marginLeft(10.px)
        }
    }

    val primaryButton by style {
        backgroundColor(Theme.buttonBackgroundPrimary.value())
        color(Theme.buttonForegroundPrimary.value())
        border(1.px, LineStyle.Solid, Theme.buttonBackgroundPrimary.value(Color.black))
        self + hover style {
            backgroundColor(Theme.buttonBackgroundPrimaryHover.value())
            color(Theme.buttonForegroundPrimary.value())
            border(1.px, LineStyle.Solid, Theme.buttonBackgroundPrimaryHover.value(Color.black))
        }
    }
    val disabledButton by style {
        color(Theme.disabledTextColor.value())
        cursor("inherit")

        border(1.px, LineStyle.Solid, Theme.disabledTextColor.value(Color.black))

        self + hover style {
            backgroundColor(Theme.buttonBackgroundNormal.value())
            color(Theme.disabledTextColor.value())
        }
    }
}

/**
 * Defines the button configuration
 */
class ButtonConfiguration(
    var title: String? = null,
    var primary: Boolean = false,
    var action: suspend () -> Unit = {}
) {
    /**
     * If true, when the action is called, an indeterminate progress icon is
     * displayed on the button that will be removed when the action handler returns.
     * Since the action block is suspended, it should not return until the operation
     * is complete.
     */
    var showProgressOnAction: Boolean = false

    /**
     * Renders the button as a link
     */
    var link: String? = null

    var enabled: Boolean = true

    fun action(block: suspend () -> Unit) {
        action = block
    }
}

/**
 * Render a button with a configuration callback block.
 */
@Composable
fun button(block: ButtonConfiguration.() -> Unit) {
    val config = ButtonConfiguration().apply(block)
    button(config)
}

/**
 * Render a button with a provided button configuration.
 */
@Composable
fun button(config: ButtonConfiguration) {
    var isProcessingAction by mutableStateOf(false)
    val coroutineScope = rememberCoroutineScope()

    Button({
        classes(ButtonStyles.button)
        if (!config.enabled) {
            classes(ButtonStyles.disabledButton)
        } else if (config.primary) {
            classes(ButtonStyles.primaryButton)
        }
        onClick {
            it.preventDefault()
            if (config.enabled && !isProcessingAction) { // don't allow multiple clicks
                if (config.link != null) {
                    Application.navigation.navigate(config.link!!)
                } else {
                    coroutineScope.launch {
                        isProcessingAction = true
                        try {
                            config.action()
                        } finally {
                            isProcessingAction = false
                        }
                    }
                }
            }
        }
    }) {
        Span {
            config.title?.let {
                Text(it)
            }
            if (config.showProgressOnAction && isProcessingAction) {
                Span({
                    style {
                        position(Position.Absolute)
                        top(1.px)
                        right(1.px)
                    }
                }) {
                    spinner(
                        style = if (config.primary) Style.LIGHT else Style.DARK,
                        size = Size.SMALL
                    )
                }
            }
        }
    }
}

/**
 * Simplified button rendered where a title and action is provided.
 */
@Composable
fun button(title: String, primary: Boolean = false, actionProgress: Boolean = false, action: suspend () -> Unit) {
    button {
        this.title = title
        this.primary = primary
        this.showProgressOnAction = actionProgress
        this.action(action)
    }
}